import React, { useEffect, useState } from 'react';
import AddressManagementService, { Address, CorrectFullAddressResponse } from '../../services/addressManagementService';

interface CorrectFullAddressModalProps {
    isOpen: boolean;
    onClose: () => void;
    traceId: string;
    address: Address;
    correctedBy: string;
    onSuccess: (response: CorrectFullAddressResponse) => void;
}

const CorrectFullAddressModal: React.FC<CorrectFullAddressModalProps> = ({
    isOpen,
    onClose,
    traceId,
    address,
    correctedBy,
    onSuccess
}) => {
    const [street1, setStreet1] = useState('');
    const [street2, setStreet2] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');
    const [reason, setReason] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setStreet1(address.street1 || '');
            setStreet2(address.street2 || '');
            setCity(address.city || '');
            setState(address.stateCode || '');
            setZip(address.zip || '');
            setReason('');
            setError(null);
        }
    }, [isOpen, address]);

    if (!isOpen) return null;

    // Mirrors the API rule: a record valid on both providers stays live when corrected,
    // while an invalid one is superseded and its input redirected to the correction
    const addressStaysLive = address.isGoogleValid === true && address.isRadarValid === true;

    const isValid =
        street1.trim().length > 0 &&
        city.trim().length > 0 &&
        state.trim().length === 2 &&
        zip.trim().length > 0;

    const handleSubmit = async () => {
        if (!isValid) return;
        setIsSaving(true);
        setError(null);
        try {
            const response = await AddressManagementService.correctFullAddress(traceId, {
                street1: street1.trim(),
                street2: street2.trim() || undefined,
                city: city.trim(),
                state: state.trim().toUpperCase(),
                zip: zip.trim(),
                correctedBy,
                reason: reason.trim() || undefined
            });
            onSuccess(response);
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message || 'Failed to correct address');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div
            className="modal fade show"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
            tabIndex={-1}
            role="dialog"
            onClick={onClose}
        >
            <div className="modal-dialog modal-lg modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                <div className="modal-content border-0 shadow-lg">
                    <div className="modal-header border-bottom">
                        <h5 className="modal-title">
                            <i className="bi bi-house-gear me-2"></i>Correct Full Address
                        </h5>
                        <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        {addressStaysLive ? (
                            <div className="alert alert-info small py-2">
                                <i className="bi bi-info-circle-fill me-2"></i>
                                This validates the corrected address and creates a <strong>new record with a new
                                Trace ID</strong>. The current record is <strong>valid on both providers</strong>,
                                so it stays live and keeps answering for its own address. Any redirects pointing
                                at it will follow the correction to the new record.
                            </div>
                        ) : (
                            <div className="alert alert-warning small py-2">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                This validates the corrected address and creates a <strong>new record with a new
                                Trace ID</strong>. The current record is superseded, and future validations of the
                                old address input will automatically resolve to the corrected record.
                            </div>
                        )}

                        <div className="mb-3 p-2 bg-light border rounded small">
                            <span className="text-muted">Correcting:</span>{' '}
                            <span className="fw-medium">
                                {address.street1}{address.street2 ? `, ${address.street2}` : ''}, {address.city}, {address.stateCode} {address.zip}
                            </span>
                        </div>

                        <div className="row g-2 mb-2">
                            <div className="col-md-8">
                                <label className="form-label small fw-bold">Street 1 *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    maxLength={150}
                                    value={street1}
                                    onChange={(e) => setStreet1(e.target.value)}
                                    disabled={isSaving}
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label small fw-bold">Street 2</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    maxLength={150}
                                    value={street2}
                                    onChange={(e) => setStreet2(e.target.value)}
                                    disabled={isSaving}
                                />
                            </div>
                        </div>

                        <div className="row g-2 mb-3">
                            <div className="col-md-6">
                                <label className="form-label small fw-bold">City *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    maxLength={50}
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    disabled={isSaving}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label small fw-bold">State *</label>
                                <input
                                    type="text"
                                    className="form-control text-uppercase"
                                    maxLength={2}
                                    value={state}
                                    onChange={(e) => setState(e.target.value)}
                                    disabled={isSaving}
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label small fw-bold">Zip *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    maxLength={10}
                                    value={zip}
                                    onChange={(e) => setZip(e.target.value)}
                                    disabled={isSaving}
                                />
                            </div>
                        </div>

                        <div className="mb-2">
                            <label className="form-label small fw-bold">Reason</label>
                            <textarea
                                className="form-control"
                                rows={2}
                                maxLength={500}
                                placeholder="Why is this address being corrected?"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                disabled={isSaving}
                            />
                        </div>

                        {error && (
                            <div className="alert alert-danger small py-2 mb-0">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>{error}
                            </div>
                        )}
                    </div>
                    <div className="modal-footer border-top">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSaving}>
                            Cancel
                        </button>
                        <button type="button" className="btn btn-warning" onClick={handleSubmit} disabled={!isValid || isSaving}>
                            {isSaving ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Validating & Correcting...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-lg me-2"></i>Correct Address
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CorrectFullAddressModal;
